<template>
  <div
    class="login-scan-container"
    :class="{ 'is-popover': isPopover }"
    v-loading="isLoading"
  >
    <!-- 二维码主体区域 -->
    <div class="code-box">
      <img v-if="codeUrl" :src="codeUrl" class="code-img" alt="二维码" />

      <!-- 失效遮罩层：贴合在 code-box 内部 -->
      <div class="code-overlay flex-centered" v-if="isExpire">
        <div class="overlay-text font24">
          <p>绑定超时</p>
          <p>二维码失效</p>
          <el-button
            class="refresh-button button"
            :size="isPopover ? 'mini' : 'medium'"
            @click="getThirdPartyLoginCode"
          >
            刷 新
          </el-button>
        </div>
      </div>
    </div>

    <!-- 底部说明文案 -->
    <div class="code-info font14">
      <p class="code-info__title">{{ text1 }}</p>
      <p class="code-info__subtitle">{{ text2 }}</p>
    </div>
  </div>
</template>

<script>
import { createSocket, setUid, closeSocket } from "@/api/socket.js";
import { getUserInfo } from "@/api/dataService";

const DEFAULT_COUNTDOWN_SECONDS = 5 * 60;

export default {
  name: "LoginScanCode",
  props: {
    type: {
      type: String,
      default: "default",
    },
    platform: String,
    api: {
      type: String,
      default: "getThirdPartyLoginCode",
    },
    text1: {
      type: String,
      default: "使用微信扫一扫",
    },
    text2: {
      type: String,
      default: "登录用户中心",
    },
  },
  data() {
    return {
      isLoading: false,
      countdown: 0,
      timer: null,
      isExpire: false,
      codeUrl: "",
    };
  },
  computed: {
    /**
     * 是否为气泡弹窗精简模式
     */
    isPopover() {
      return this.type === "popover";
    },
  },
  created() {
    this.getThirdPartyLoginCode();
  },
  beforeDestroy() {
    clearInterval(this.timer);
    window.removeEventListener("onMessageWS", this.websocketMsg);
    closeSocket();
  },
  methods: {
    /**
     * 获取第三方登录二维码
     */
    async getThirdPartyLoginCode() {
      this.isExpire = false;
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }

      this.isLoading = true;
      try {
        const [res, err] = await this.$get(this.api, {
          platform: this.platform,
        });
        if (err) return this.$msg(err);

        this.codeUrl = res.url;
        this.initSocket(res);
        this.startCountdown(DEFAULT_COUNTDOWN_SECONDS);
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * 获取用户信息
     */
    async getUserInfo() {
      const [user, err] = await getUserInfo();
      this.$emit("ok", user);
    },
    /**
     * 初始化 socket
     * @param {Object} data 接口返回的数据对象，包含 uuid
     */
    initSocket(data) {
      setUid(data.uuid);
      createSocket();
      window.removeEventListener("onMessageWS", this.websocketMsg);
      window.addEventListener("onMessageWS", this.websocketMsg);
    },
    /**
     * WebSocket 消息通知
     * @param {CustomEvent} event 自定义消息事件
     */
    websocketMsg(event) {
      const data = event.detail;
      switch (data.event) {
        case "onLoginCodeScan":
          if (data.data.is_login == 2) return this.$msg.warning(data.data.msg);
          this.$cookie.set("TOKEN", data.data.token);
          this.getUserInfo();
          break;
        case "onBindCodeScan":
          if (data.data.is_bind == 2) return this.$msg.warning(data.data.msg);
          this.$emit("ok");
          break;
        default:
          break;
      }
    },
    /**
     * 开始倒计时
     * @param {number} seconds 倒计时时长（秒）
     */
    startCountdown(seconds) {
      this.countdown = seconds;
      this.isExpire = false;
      this.timer = setInterval(() => {
        if (this.countdown > 1) {
          this.countdown--;
        } else {
          this.countdown = 0;
          this.isExpire = true;
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 1000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/login-form.scss";

.login-scan-container {
  position: relative;
  width: 100%;
  min-height: 350px;
  box-sizing: border-box;

  /* popover 紧凑卡片模式 */
  &.is-popover {
    width: 200px;
    min-height: auto;
    padding: 24px 14px 22px;
    background: #ffffff;
    border: 1px solid #ebeef5;
    border-radius: 16px;

    .code-box {
      width: 150px;
      height: 150px;
      border: none;
      border-radius: 8px;
    }

    .code-info {
      margin-top: 8px;

      &__title {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 2px;
      }

      &__subtitle {
        font-size: 13px;
        color: #4a4a4a;
      }
    }

    .overlay-text {
      p {
        font-size: 14px;
      }
    }

    .refresh-button {
      margin-top: 8px;
      width: 90px;
      height: 28px;
      padding: 0;
      font-size: 12px;
    }
  }
}

.code-box {
  position: relative;
  width: 288.6px;
  height: 288.6px;
  margin: 0 auto;
  box-sizing: border-box;
  border: 1px solid #d7d9dc;
  overflow: hidden;

  .code-img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.code-info {
  line-height: 1.4;
  margin-top: 12px;
  text-align: center;

  &__title {
    color: #15a74c;
    margin: 0;
  }

  &__subtitle {
    color: #2c2c2c;
    margin: 0;
  }
}

.code-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-text {
  line-height: 1.4;
  color: #fff;
  font-weight: bold;
  text-align: center;

  p {
    font-size: 24px;
    margin: 0;
  }
}

.refresh-button {
  margin-top: 16px;
  width: 160px;
  background: linear-gradient(109deg, #01c7ff 0%, #0296ff 102%);
  border: none;
  color: #ffffff !important;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus {
    background: linear-gradient(109deg, #01c7ff 0%, #0296ff 102%) !important;
    color: #ffffff !important;
    border: none !important;
    opacity: 1;
  }

  &:active {
    opacity: 1;
  }
}
</style>